import React, { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, RefreshCw, X } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';

interface SlotLightboxProps {
  onSaveImage: (resultId: string) => void;
  onReroll: (resultId: string) => void;
}

export const SlotLightbox: React.FC<SlotLightboxProps> = ({ onSaveImage, onReroll }) => {
  const lightboxIndex = useSlotAssetGeneratorStore((s) => s.lightboxIndex);
  const results = useSlotAssetGeneratorStore((s) => s.generation.results);
  const request = useSlotAssetGeneratorStore((s) => s.generation.request);
  const closeLightbox = useSlotAssetGeneratorStore((s) => s.closeLightbox);
  const openLightbox = useSlotAssetGeneratorStore((s) => s.openLightbox);

  const currentIndex = lightboxIndex ?? 0;
  const currentResult = results[currentIndex];
  const total = results.length;

  const goPrev = useCallback(() => {
    openLightbox(currentIndex > 0 ? currentIndex - 1 : total - 1);
  }, [currentIndex, total, openLightbox]);

  const goNext = useCallback(() => {
    openLightbox(currentIndex < total - 1 ? currentIndex + 1 : 0);
  }, [currentIndex, total, openLightbox]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeLightbox, goPrev, goNext]);

  if (lightboxIndex === null || !currentResult) return null;

  return (
    <div
      className="absolute inset-0 z-[110] bg-black/80 flex flex-col"
      onClick={closeLightbox}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm text-zinc-400">
          {currentIndex + 1} / {total}
        </div>
        <button
          onClick={closeLightbox}
          className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Image area */}
      <div
        className="flex-1 flex items-center justify-center relative px-12"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Nav arrows */}
        {total > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 p-2 text-zinc-400 hover:text-white rounded-full hover:bg-zinc-700/50 transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 p-2 text-zinc-400 hover:text-white rounded-full hover:bg-zinc-700/50 transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Image with checkerboard */}
        <div
          className="max-w-full max-h-full rounded-lg overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
          }}
        >
          {currentResult.imageUrl && (
            <img
              src={currentResult.imageUrl}
              alt={`Result ${currentIndex + 1}`}
              className="max-w-full max-h-[70vh] object-contain"
            />
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="flex items-center justify-between px-4 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Metadata */}
        <div className="text-xs text-zinc-500 space-x-3">
          {request && (
            <>
              <span>{request.component.width ?? '?'}x{request.component.height ?? '?'}</span>
              <span>{request.component.format}</span>
            </>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onSaveImage(currentResult.id)}
            disabled={currentResult.status === 'generating' || currentResult.status === 'error'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 hover:bg-green-500 text-white rounded disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors"
          >
            <Download size={12} />
            Use This
          </button>
          <button
            onClick={() => onReroll(currentResult.id)}
            disabled={currentResult.status === 'generating'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors"
          >
            <RefreshCw size={12} />
            Re-roll
          </button>
          <button
            onClick={closeLightbox}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 border border-zinc-600 rounded hover:bg-zinc-800 transition-colors"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};
